// ─────────────────────────────────────────────────────────────────────
// BANEPAD DEVICE SESSION — the physical tablet's local PAIRING NOTE.
//
// ⚠️ NOT A CREDENTIAL STORE ANY MORE (#663). The header that stood here said it
// was the pad's «local token store» and that «what remains is this file: the
// credential AT REST on the tablet». Both were true when written and are false
// now, which is exactly the shape #664 existed to stop — a security warning
// outliving the thing it warned about, above the implementation that fixed it.
//
// WHAT IS TRUE NOW: the device credential is an httpOnly `spd_session` cookie
// issued by the API (#666). This file holds a FLAG saying this tablet has been
// paired, plus the pre-cookie token for as long as one survives a migration.
//
// ── WHY `window.localStorage` DIRECTLY, STILL ───────────────────────
//
//   • The shared `Storage` abstraction is tenant-scoped and, in production,
//     backed by the club's shared store — i.e. SHARED across devices. Anything
//     about THIS tablet must live only on this tablet; syncing it would tell
//     every pad in the club that it had been paired.
//   • It mirrors the auth gate (src/app/Padel24App.tsx), which keeps its session
//     flag in `sessionStorage` directly for the same reason.
//
// ── THE DECISION THAT GOT US HERE, for the record ───────────────────
//
// Recorded on #663: an httpOnly, Secure, SameSite=Lax, host-only cookie issued by
// the API — NOT an Android keystore, which would have protected a native shell
// while the deployed browser path kept the token in plaintext here. The
// consequence came with it: an AMBIENT credential means every state-changing
// `/pad` route needs its own trusted-Origin rule, and device auth stays its own
// credential family rather than inheriting the club/member middleware (#666).
//
// ── WHAT A LEAK OF WHAT IS LEFT HERE WOULD COST ─────────────────────
//
// The flag grants nothing: setting it by hand gets you the paired SCREEN and a
// 401 from every request behind it. A surviving LEGACY token still grants one
// court's Banepad view and heartbeat, in one club, until an operator revokes it —
// because the scope comes from the device row, not from the token — and it is
// deleted the first time the tablet successfully talks to the server.
// ─────────────────────────────────────────────────────────────────────

// ═══════════════════════════════════════════════════════════════════
// TWO KEYS, AND THE SECOND ONE IS ON ITS WAY OUT (#663).
//
// `banepad_device_paired_v1` is a FLAG, not a credential: it answers «has this
// tablet been paired» so the pad can decide what to render before it has spoken
// to anybody. That question used to be answered by the presence of the token,
// which is why the token had to be readable at all.
//
// The credential itself now travels as an httpOnly `spd_session` cookie the pad
// cannot read (#666). So the flag is all that is left here, and it grants
// nothing: setting it by hand gets you the paired SCREEN and a 401 from every
// request behind it.
//
// ── WHY THE LEGACY TOKEN IS STILL READ ──────────────────────────────
//
// A tablet paired before #666 has `banepad_device_token_v1` in its storage and no
// cookie. Deleting the token on sight would unpair it: the cookie is seeded by
// `requireDeviceAuth` on a successful BEARER request, so the pad has to make one
// FIRST, with the token it already holds.
//
// Hence a prove-then-delete handshake, not a deletion:
//
//   1. the pad boots, reads the legacy token, and sends it as a bearer;
//   2. the server answers and — per #666 — sets `spd_session` on that response;
//   3. `retireLegacyDeviceToken()` is called on that success: the flag goes in,
//      the token comes out of storage;
//   4. every later request carries the cookie and no header at all.
//
// Step 3 happens only after a request has SUCCEEDED, which is what makes it safe.
// It is the same trap the server side had: «by then every pad has a cookie» is a
// claim about a mechanism, and the mechanism has to exist (#666 review).
// ═══════════════════════════════════════════════════════════════════

/** localStorage key for «this tablet has been paired». NOT a credential. */
export const BANEPAD_DEVICE_PAIRED_STORAGE_KEY = 'banepad_device_paired_v1';

/** localStorage key for the pre-#663 device token. Read to migrate, never written. */
export const BANEPAD_DEVICE_TOKEN_STORAGE_KEY = 'banepad_device_token_v1';

function readKey(key: string): string | null {
  try {
    const value = window.localStorage.getItem(key);
    return value && value.trim() ? value : null;
  } catch {
    // Private mode / storage disabled — treat as absent.
    return null;
  }
}

/**
 * Has this tablet been paired?
 *
 * TRUE FOR A LEGACY TOKEN TOO, which is the whole migration: a pad that has not
 * yet made its first request since #666 has only the token, and answering «no»
 * would send it to the pairing screen with a perfectly good credential in its
 * pocket.
 */
export function isBanepadDevicePaired(): boolean {
  return readKey(BANEPAD_DEVICE_PAIRED_STORAGE_KEY) !== null || legacyDeviceToken() !== null;
}

/** Records that this tablet is paired. Called after the server has said so. */
export function markBanepadDevicePaired(): void {
  try {
    window.localStorage.setItem(BANEPAD_DEVICE_PAIRED_STORAGE_KEY, '1');
  } catch {
    /* storage unavailable — nothing else we can do on a kiosk */
  }
}

/**
 * The pre-#663 token, for the transitional bearer only.
 *
 * Nothing writes this any more. It exists so a pad paired before the cookie can
 * authenticate once and be seeded — see the handshake above.
 */
export function legacyDeviceToken(): string | null {
  return readKey(BANEPAD_DEVICE_TOKEN_STORAGE_KEY);
}

/**
 * The handshake's third step: a request has succeeded, so the cookie is set.
 *
 * Marks the pad paired and takes the raw token out of storage. Idempotent, and a
 * no-op once there is no legacy token left — which is every pad paired after
 * #666 and every pad that has made one request since.
 */
export function retireLegacyDeviceToken(): void {
  markBanepadDevicePaired();
  try {
    window.localStorage.removeItem(BANEPAD_DEVICE_TOKEN_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Forget that this tablet is paired — the «Nullstill denne paden» reset.
 *
 * Clears both keys. The server-side registration is a SEPARATE concept (see
 * `revokeBanepadDevice`); this does not touch it, and it cannot clear the
 * httpOnly cookie either — the server does that when it refuses one (#666).
 */
export function clearBanepadDevicePairing(): void {
  try {
    window.localStorage.removeItem(BANEPAD_DEVICE_PAIRED_STORAGE_KEY);
    window.localStorage.removeItem(BANEPAD_DEVICE_TOKEN_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
